/**
 * Compliance Pages — body content for the four public compliance/policy
 * pages (/compliance, /privacy-policy, /terms-of-use, /equal-housing —
 * see CompliancePage.tsx). ADMIN and DEVELOPER, via `compliance-pages:write`
 * (see roles.ts's doc comment for why this one isn't ADMIN-only like most
 * of this app's other narrow settings grants).
 *
 * One mutable row per kind, edited in place — no version history, unlike
 * Document Templates. `audit_logs` is the history mechanism here (every
 * PATCH calls request.audit()). Page titles stay fixed in code
 * (compliance-page-titles.ts); only the body is editable.
 *
 * A kind with no saved row yet shows the exact placeholder text the public
 * page falls back to, read-only, so staff can see what a visitor sees
 * before anything is published for that kind — same "starts empty, falls
 * back to a disclosed placeholder" posture as Document Templates.
 */

import { useEffect, useState } from 'react';
import { COMPLIANCE_PAGE_KINDS, type CompliancePageDto, type CompliancePageKind } from 'shared';
import { api } from '../../lib/api.js';
import { useCan } from '../../lib/capabilities.js';
import { COMPLIANCE_PAGE_TITLES } from '../../lib/compliance-page-titles.js';
import { Field, FormErrors } from '../../components/Form.js';
import { RichTextEditor } from '../../components/RichTextEditor.js';

const PLACEHOLDER_BODY =
  'This page is a placeholder. {title} content will be provided by the firm and published here before this site goes live.';

export function CompliancePagesPage() {
  const canWrite = useCan('compliance-pages:write');

  const [pages, setPages] = useState<Map<CompliancePageKind, CompliancePageDto> | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);

  const [kind, setKind] = useState<CompliancePageKind>(COMPLIANCE_PAGE_KINDS[0]);
  const [body, setBody] = useState('');
  // Tiptap's `content` only seeds the editor's *initial* document, so
  // switching kinds needs a remount to load the newly-selected kind's body —
  // same mechanism DocumentTemplateList.tsx uses after a publish.
  const [bodyEditorKey, setBodyEditorKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api<CompliancePageDto[]>('/staff/compliance-pages')
      .then((rows) => {
        if (cancelled) return;
        setPages(new Map(rows.map((row) => [row.kind, row])));
      })
      .catch((caught: unknown) => {
        if (!cancelled) setLoadError(caught);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!pages) return;
    setBody(pages.get(kind)?.body ?? '');
    setBodyEditorKey((k) => k + 1);
    setSaved(false);
    setSaveError(null);
  }, [kind, pages]);

  async function save() {
    setBusy(true);
    setSaveError(null);
    setSaved(false);
    try {
      const updated = await api<CompliancePageDto>(`/staff/compliance-pages/${kind}`, {
        method: 'PATCH',
        body: JSON.stringify({ body }),
      });
      setPages((previous) => {
        const next = new Map(previous ?? []);
        next.set(kind, updated);
        return next;
      });
      setSaved(true);
    } catch (caught) {
      setSaveError(caught);
    } finally {
      setBusy(false);
    }
  }

  if (loadError) return <FormErrors error={loadError} />;
  if (!pages) return <p className="muted">Loading…</p>;

  const current = pages.get(kind);
  const title = COMPLIANCE_PAGE_TITLES[kind];
  const placeholder = PLACEHOLDER_BODY.replace('{title}', title);

  return (
    <section className="stack">
      <h1>Compliance Pages</h1>
      <p className="muted">
        Body content for the four public compliance/policy pages. Page titles are fixed; only the
        body below is editable. A page with no saved content here shows the placeholder text below
        to visitors until something is published.
      </p>

      <Field label="Page" name="kind">
        <select
          id="kind"
          name="kind"
          className="field__input"
          value={kind}
          onChange={(event) => setKind(event.target.value as CompliancePageKind)}
        >
          {COMPLIANCE_PAGE_KINDS.map((k) => (
            <option key={k} value={k}>
              {COMPLIANCE_PAGE_TITLES[k]}
            </option>
          ))}
        </select>
      </Field>

      {canWrite ? (
        <div className="card stack">
          {saveError ? <FormErrors error={saveError} /> : null}

          {!current?.body ? (
            <p className="muted">
              Nothing published for {title} yet — the public page is showing this placeholder:{' '}
              <em>{placeholder}</em>
            </p>
          ) : null}

          <Field
            label="Body"
            name="body"
            hint="Use the toolbar to format text. This renders directly on the public site."
          >
            <RichTextEditor
              key={bodyEditorKey}
              value={body}
              onChange={setBody}
              tokenGroups={[]}
              tokenGroupsNote="Compliance pages don't support merge fields."
            />
          </Field>

          {saved ? <p className="muted">Saved.</p> : null}

          <div className="row">
            <button type="button" className="button" disabled={busy} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <div className="card stack">
          <p className="muted">{title}</p>
          {current?.body ? (
            <div dangerouslySetInnerHTML={{ __html: current.body }} />
          ) : (
            <p className="muted">
              <em>{placeholder}</em>
            </p>
          )}
        </div>
      )}
    </section>
  );
}
